import React, { FC, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  TrainerPackage,
  PackagePresetTier,
  PACKAGE_PRESET_TEMPLATES,
  getPackageTemplateConfig,
  validatePackageClient,
  validatePackageDraftClient,
  validateReadyWithinTimeline,
  VALID_CURRENCIES,
  CURRENCY_SYMBOLS,
  VALID_DURATIONS,
} from 'src/api/services/packagesService';
import { TemplateType } from './DocumentTemplateView';
import { CustomSelect } from './CustomSelect';
import { ReactComponent as ProgramIcon } from 'src/assets/images/onboarding-trainer/dashboard/profile/packages/packages-list/dumbbell-program.svg';
import { ReactComponent as ChatIcon } from 'src/assets/images/onboarding-trainer/dashboard/profile/packages/packages-list/inquiry-chat.svg';
import { ReactComponent as VideoIcon } from 'src/assets/images/onboarding-trainer/dashboard/profile/packages/packages-list/video-checkin.svg';
import { ReactComponent as CalendarIcon } from 'src/assets/images/onboarding-trainer/dashboard/profile/packages/create-edit-package/calendar-period.svg';
import { ReactComponent as DocIcon } from 'src/assets/images/onboarding-trainer/dashboard/profile/packages/create-edit-package/document-policy.svg';
import { ReactComponent as RefreshIcon } from 'src/assets/images/onboarding-trainer/dashboard/profile/packages/create-edit-package/no-automatic-renewal.svg';
import { ReactComponent as AppleIcon } from 'src/assets/images/onboarding-trainer/dashboard/profile/packages/create-edit-package/nutritional-apple.svg';
import { ReactComponent as LockIcon } from 'src/assets/images/onboarding-trainer/dashboard/profile/packages/create-edit-package/lock-security.svg';
import { ReactComponent as PackageDetailsIcon } from 'src/assets/images/onboarding-trainer/dashboard/profile/packages/create-edit-package/package-details.svg';
import { ReactComponent as InquiryOrangeIcon } from 'src/assets/images/onboarding-trainer/dashboard/profile/packages/create-edit-package/inquiry-orange.svg';
import { ReactComponent as OptionalExtrasIcon } from 'src/assets/images/onboarding-trainer/dashboard/profile/packages/create-edit-package/optional-extras.svg';
import { ReactComponent as DeliveryExpectationsIcon } from 'src/assets/images/onboarding-trainer/dashboard/profile/packages/create-edit-package/delivery-expectations.svg';
import { ReactComponent as PackageTermsIcon } from 'src/assets/images/onboarding-trainer/dashboard/profile/packages/create-edit-package/package-terms.svg';
import { ReactComponent as CheckmarkGreenIcon } from 'src/assets/images/onboarding-trainer/dashboard/profile/packages/create-edit-package/checkmark-green.svg';
import { auth } from 'src/config/firebase';
import { sessionStore } from 'src/store/sessionStore';
import toast from 'src/utils/appAlert';
import './PackageForm.scss';

interface PackageFormProps {
  mode: 'create' | 'edit';
  initialData?: Partial<TrainerPackage> | null;
  initialTier?: PackagePresetTier;
  allPackages?: TrainerPackage[];
  onTierChange?: (newTier: PackagePresetTier, targetPackageId?: string) => void;
  onBack: () => void;
  onSave: (pkgData: Partial<TrainerPackage>) => void;
  onAutoSave?: (
    pkgData: Partial<TrainerPackage>,
    signal?: AbortSignal,
  ) => Promise<TrainerPackage | void>;
  onUnpublishClick?: () => void;
  onOpenTemplate: (type: TemplateType, currentDraft?: Partial<TrainerPackage>) => void;
  isSaving?: boolean;
}

const PRESET_OPTIONS: PackagePresetTier[] = PACKAGE_PRESET_TEMPLATES.map((opt) => opt.tier);
const READY_WITHIN_OPTIONS = [
  'Within 24 hours',
  'Within 48 hours',
  'Within 3 days',
  'Within 5 days',
  'Within 7 days',
];
const NUTRITIONAL_DELIVERY_OPTIONS = [
  'Within 24 hours',
  'Within 48 hours',
  'Within 3 days',
  'Within 5 days',
];

const getSavedDraft = (key: string): Partial<TrainerPackage> | null => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Partial<TrainerPackage>) : null;
  } catch {
    return null;
  }
};

export const PackageForm: FC<PackageFormProps> = ({
  mode,
  initialData,
  initialTier,
  allPackages = [],
  onTierChange,
  onBack,
  onSave,
  onAutoSave,
  onUnpublishClick,
  onOpenTemplate,
  isSaving = false,
}) => {
  const { currentUser } = sessionStore();
  const trainerId = currentUser?.uid || auth.currentUser?.uid || '';
  const draftStorageKey = `mini_pt_package_create_draft_${trainerId}`;

  // Read saved draft once during initial state computation for create mode
  const initialOrDraft = useMemo(() => {
    if (mode === 'create' && !initialData) {
      const saved = getSavedDraft(draftStorageKey);
      if (saved && (saved.name === 'Basic' || saved.price === 95)) {
        try {
          localStorage.removeItem(draftStorageKey);
        } catch {
          // ignore
        }
        return null;
      }
      return saved;
    }
    return initialData || null;
  }, [mode, initialData, draftStorageKey]);

  const [draftId, setDraftId] = useState<string | undefined>(initialOrDraft?.id);

  const defaultTierConfig = getPackageTemplateConfig(initialOrDraft?.presetTier || initialTier);

  // Preset Tier
  const [presetTier, setPresetTier] = useState<PackagePresetTier>(
    initialOrDraft?.presetTier || initialTier || defaultTierConfig.tier,
  );

  const [templateId, setTemplateId] = useState<string>(
    initialOrDraft?.templateId || defaultTierConfig.templateId,
  );

  // Form Fields - Blank by default, never prefilled with dummy text
  const [name, setName] = useState<string>(initialOrDraft?.name ?? '');
  const [shortDescription, setShortDescription] = useState<string>(
    initialOrDraft?.shortDescription ?? '',
  );
  const [whoIsThisFor, setWhoIsThisFor] = useState<string>(initialOrDraft?.whoIsThisFor ?? '');
  const [price, setPrice] = useState<number | string>(
    initialOrDraft?.price !== undefined ? initialOrDraft.price : '',
  );
  const [currency, setCurrency] = useState<string>(initialOrDraft?.currency || 'GBP');
  const [duration, setDuration] = useState<string>(initialOrDraft?.duration || '4 weeks/ 1 month');

  // Coaching & Communication
  const [inquiriesPerWeek, setInquiriesPerWeek] = useState<number>(
    initialOrDraft?.coaching?.inquiriesPerWeek ?? 1,
  );
  const [checkInsPerWeek, setCheckInsPerWeek] = useState<number>(
    initialOrDraft?.coaching?.checkInsPerWeek ?? 1,
  );

  // Optional Extras
  const [nutritionalPlan, setNutritionalPlan] = useState<boolean>(
    initialOrDraft?.optionalExtras?.nutritionalPlan !== undefined
      ? Boolean(initialOrDraft.optionalExtras.nutritionalPlan)
      : false,
  );
  const [nutritionalGuidance, setNutritionalGuidance] = useState<boolean>(
    initialOrDraft?.optionalExtras?.nutritionalGuidance !== undefined
      ? Boolean(initialOrDraft.optionalExtras.nutritionalGuidance)
      : false,
  );

  // Delivery & Expectations
  const [initialProgramReadyWithin, setInitialProgramReadyWithin] = useState<string>(
    initialOrDraft?.delivery?.initialProgramReadyWithin || '',
  );
  const [nutritionalPlanDelivery, setNutritionalPlanDelivery] = useState<string>(
    initialOrDraft?.delivery?.nutritionalPlanDelivery || 'Within 3 days',
  );

  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const isDirtyRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const latestDraftRef = useRef<Partial<TrainerPackage>>({});

  const markDirty = () => {
    isDirtyRef.current = true;
  };

  // Validation Errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Field-level validation on blur
  const validateField = (fieldName: string, value: unknown) => {
    const tempPayload: Partial<TrainerPackage> = {
      name,
      shortDescription,
      whoIsThisFor,
      price: price === '' ? undefined : Number(price),
      currency,
      duration,
      presetTier,
      optionalExtras: {
        nutritionalPlan,
        nutritionalGuidance,
      },
      delivery: {
        initialProgramReadyWithin,
        nutritionalPlanDelivery,
      },
      [fieldName]: value,
    };
    const { errors: allErrors } = validatePackageClient(tempPayload);
    setErrors((prev) => {
      const next = { ...prev };
      if (allErrors[fieldName]) {
        next[fieldName] = allErrors[fieldName];
      } else {
        delete next[fieldName];
      }
      return next;
    });
  };

  // When changing preset tier in header
  const handlePresetChange = (newTier: string) => {
    const tier = newTier as PackagePresetTier;

    // Clear any pending debounced autosave from previous tier
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    isDirtyRef.current = false;
    setIsAutoSaving(false);

    // Look for existing package matching this tier in allPackages
    const foundPkg = allPackages.find((p) => p.presetTier.toLowerCase() === tier.toLowerCase());
    const tierConfig = getPackageTemplateConfig(tier);

    if (foundPkg) {
      // Package found -> set that package's data
      setDraftId(foundPkg.id);
      setPresetTier(foundPkg.presetTier);
      setTemplateId(foundPkg.templateId || tierConfig.templateId);
      setName(foundPkg.name ?? '');
      setShortDescription(foundPkg.shortDescription ?? '');
      setWhoIsThisFor(foundPkg.whoIsThisFor ?? '');
      setPrice(
        foundPkg.price !== undefined && foundPkg.price !== 0
          ? foundPkg.price
          : foundPkg.price === 0
            ? 0
            : '',
      );
      setCurrency(foundPkg.currency || 'GBP');
      setDuration(foundPkg.duration || '4 weeks/ 1 month');
      setInquiriesPerWeek(foundPkg.coaching?.inquiriesPerWeek ?? 1);
      setCheckInsPerWeek(foundPkg.coaching?.checkInsPerWeek ?? 1);
      setNutritionalPlan(Boolean(foundPkg.optionalExtras?.nutritionalPlan));
      setNutritionalGuidance(Boolean(foundPkg.optionalExtras?.nutritionalGuidance));
      setInitialProgramReadyWithin(foundPkg.delivery?.initialProgramReadyWithin || '');
      setNutritionalPlanDelivery(foundPkg.delivery?.nutritionalPlanDelivery || 'Within 3 days');
      setErrors({});
      isDirtyRef.current = false;
      onTierChange?.(tier, foundPkg.id);
    } else {
      // Package not found -> reset form data
      setDraftId(undefined);
      setPresetTier(tier);
      setTemplateId(tierConfig.templateId);
      setName('');
      setShortDescription('');
      setWhoIsThisFor('');
      setPrice('');
      setCurrency('GBP');
      setDuration('4 weeks/ 1 month');
      setInquiriesPerWeek(1);
      setCheckInsPerWeek(1);
      setNutritionalPlan(false);
      setNutritionalGuidance(false);
      setInitialProgramReadyWithin('');
      setNutritionalPlanDelivery('Within 3 days');
      setErrors({});
      isDirtyRef.current = false;
      onTierChange?.(tier, undefined);
    }
  };

  // Re-hydrate when initialData changes
  useEffect(() => {
    // If the user has active unsaved edits in progress, NEVER overwrite live inputs!
    if (isDirtyRef.current) {
      if (initialData?.id) {
        setDraftId((prev) => prev || initialData.id);
      }
      return;
    }

    if (initialData) {
      if (initialData.id) {
        setDraftId(initialData.id);
      }
      const cfg = getPackageTemplateConfig(initialData.presetTier || initialTier);
      setPresetTier(initialData.presetTier || cfg.tier);
      setTemplateId(initialData.templateId || cfg.templateId);
      setName(initialData.name ?? '');
      setShortDescription(initialData.shortDescription ?? '');
      setWhoIsThisFor(initialData.whoIsThisFor ?? '');
      setPrice(
        initialData.price !== undefined && initialData.price !== 0
          ? initialData.price
          : initialData.price === 0
            ? 0
            : '',
      );
      setCurrency(initialData.currency || 'GBP');
      setDuration(initialData.duration || '4 weeks/ 1 month');
      setInquiriesPerWeek(initialData.coaching?.inquiriesPerWeek ?? 1);
      setCheckInsPerWeek(initialData.coaching?.checkInsPerWeek ?? 1);
      setNutritionalPlan(Boolean(initialData.optionalExtras?.nutritionalPlan));
      setNutritionalGuidance(Boolean(initialData.optionalExtras?.nutritionalGuidance));
      setInitialProgramReadyWithin(initialData.delivery?.initialProgramReadyWithin || '');
      setNutritionalPlanDelivery(initialData.delivery?.nutritionalPlanDelivery || 'Within 3 days');
      setErrors({});
    } else {
      const cfg = getPackageTemplateConfig(initialTier);
      setDraftId(undefined);
      setPresetTier(cfg.tier);
      setTemplateId(cfg.templateId);
      setName('');
      setShortDescription('');
      setWhoIsThisFor('');
      setPrice('');
      setCurrency('GBP');
      setDuration('4 weeks/ 1 month');
      setInquiriesPerWeek(1);
      setCheckInsPerWeek(1);
      setNutritionalPlan(false);
      setNutritionalGuidance(false);
      setInitialProgramReadyWithin('');
      setNutritionalPlanDelivery('Within 3 days');
      setErrors({});
    }
    // RESET dirty flag on load / initialData change - never autosave on mount!
    isDirtyRef.current = false;
    setIsAutoSaving(false);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, [initialData, initialTier]);

  // Dynamic calculated start date note
  const getCalculatedStartDate = () => {
    const validation = validateReadyWithinTimeline(initialProgramReadyWithin);
    const daysToAdd = validation.isValid && validation.days ? validation.days : 3;

    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + daysToAdd);
    return targetDate.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  // Stepper handlers
  const handleInquiriesChange = (delta: number) => {
    markDirty();
    setInquiriesPerWeek((prev) => Math.max(0, Math.min(10, prev + delta)));
  };

  const handleCheckInsChange = (delta: number) => {
    markDirty();
    setCheckInsPerWeek((prev) => Math.max(0, Math.min(10, prev + delta)));
  };

  // Get current uncommitted draft values (for navigating to document template without losing data)
  const getCurrentDraft = useCallback(
    (customStatus?: 'published' | 'draft'): Partial<TrainerPackage> => {
      const effectiveStatus: 'published' | 'draft' =
        customStatus ||
        (mode === 'create'
          ? (initialData?.status ?? 'draft')
          : (initialData?.status ?? 'published'));

      return {
        ...(draftId ? { id: draftId } : initialData?.id ? { id: initialData.id } : {}),
        templateId,
        name: name.trim(),
        presetTier,
        shortDescription: shortDescription.trim(),
        whoIsThisFor: whoIsThisFor.trim(),
        price: price === '' ? 0 : Number(price),
        currency,
        currencySymbol: CURRENCY_SYMBOLS[currency] || '£',
        duration,
        coaching: {
          inquiriesPerWeek,
          checkInsPerWeek,
        },
        optionalExtras: {
          nutritionalPlan,
          nutritionalGuidance,
        },
        delivery: {
          initialProgramReadyWithin: initialProgramReadyWithin.trim() || 'Within 3 days',
          nutritionalPlanDelivery: nutritionalPlan ? nutritionalPlanDelivery : undefined,
        },
        terms: {
          coachingPeriod: duration.includes('4 weeks')
            ? '4-week coaching period'
            : `${duration} coaching period`,
          renewal: 'No automatic renewal',
        },
        status: effectiveStatus,
      };
    },
    [
      mode,
      draftId,
      initialData,
      templateId,
      name,
      presetTier,
      shortDescription,
      whoIsThisFor,
      price,
      currency,
      duration,
      inquiriesPerWeek,
      checkInsPerWeek,
      nutritionalPlan,
      nutritionalGuidance,
      initialProgramReadyWithin,
      nutritionalPlanDelivery,
    ],
  );

  // Debounced auto-save (2000ms debounce) for create & edit mode - ONLY triggers when user edits fields
  useEffect(() => {
    const currentDraft = getCurrentDraft('draft');
    latestDraftRef.current = currentDraft;

    if (!isDirtyRef.current) {
      return;
    }

    // 1. If a debounce timer is waiting, cancel it immediately
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // 2. If an API request is in-flight, cancel it immediately so stale response cannot overwrite new typing
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // 3. Immediately persist full draft to local storage
    try {
      localStorage.setItem(draftStorageKey, JSON.stringify(currentDraft));
    } catch (e) {
      console.error('Failed to auto-save package draft to localStorage', e);
    }

    // 4. Validate client data before sending to backend:
    const draftValidation = validatePackageDraftClient(currentDraft);
    if (!draftValidation.isValid) {
      setIsAutoSaving(false);
      return;
    }

    setIsAutoSaving(true);

    // 5. 2-second debounce timer
    debounceTimerRef.current = setTimeout(async () => {
      if (!onAutoSave) {
        setIsAutoSaving(false);
        return;
      }

      // Create a fresh AbortController for this in-flight network request
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const draftToSave = latestDraftRef.current || getCurrentDraft('draft');
        const saved = await onAutoSave(draftToSave, controller.signal);
        if (controller.signal.aborted) return;
        if (saved && saved.id) {
          setDraftId(saved.id);
          try {
            localStorage.setItem(draftStorageKey, JSON.stringify({ ...draftToSave, id: saved.id }));
          } catch {
            // ignore
          }
        }
      } catch (apiErr: unknown) {
        if (
          apiErr instanceof Error &&
          (apiErr.name === 'AbortError' || apiErr.message.includes('aborted'))
        ) {
          return;
        }
        console.warn('Direct API auto-save notice:', apiErr);
      } finally {
        if (!controller.signal.aborted) {
          setIsAutoSaving(false);
        }
      }
    }, 2000);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [
    getCurrentDraft,
    draftStorageKey,
    onAutoSave,
    draftId,
    name,
    presetTier,
    shortDescription,
    whoIsThisFor,
    price,
    currency,
    duration,
    inquiriesPerWeek,
    checkInsPerWeek,
    nutritionalPlan,
    nutritionalGuidance,
    initialProgramReadyWithin,
    nutritionalPlanDelivery,
  ]);

  // Cleanup pending timer and in-flight request on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Form Validation
  const validate = (): boolean => {
    const payloadForValidation: Partial<TrainerPackage> = {
      name,
      shortDescription,
      whoIsThisFor,
      price: price === '' ? undefined : Number(price),
      presetTier,
      currency,
      duration,
      optionalExtras: {
        nutritionalPlan,
        nutritionalGuidance,
      },
      delivery: {
        initialProgramReadyWithin,
        nutritionalPlanDelivery,
      },
    };

    const result = validatePackageClient(payloadForValidation);
    setErrors(result.errors);
    return result.isValid;
  };

  // Submit Handler
  const handleSubmit = () => {
    if (!validate()) {
      toast.error('Please fix the validation errors before publishing.');
      return;
    }

    const payload: Partial<TrainerPackage> = {
      ...(draftId ? { id: draftId } : initialData?.id ? { id: initialData.id } : {}),
      templateId,
      name: name.trim(),
      presetTier,
      shortDescription: shortDescription.trim(),
      whoIsThisFor: whoIsThisFor.trim(),
      price: Number(price),
      currency,
      currencySymbol: CURRENCY_SYMBOLS[currency] || '£',
      duration,
      coaching: {
        inquiriesPerWeek,
        checkInsPerWeek,
      },
      optionalExtras: {
        nutritionalPlan,
        nutritionalGuidance,
      },
      delivery: {
        initialProgramReadyWithin,
        nutritionalPlanDelivery: nutritionalPlan ? nutritionalPlanDelivery : undefined,
      },
      terms: {
        coachingPeriod: duration.includes('4 weeks')
          ? '4-week coaching period'
          : `${duration} coaching period`,
        renewal: 'No automatic renewal',
      },
      status: 'published',
    };

    try {
      localStorage.removeItem(draftStorageKey);
    } catch {
      // ignore
    }

    onSave(payload);
  };

  const handleBack = async () => {
    if (isDirtyRef.current) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      const currentDraft = getCurrentDraft('draft');
      const draftValidation = validatePackageDraftClient(currentDraft);
      if (draftValidation.isValid && onAutoSave) {
        try {
          await onAutoSave(currentDraft);
        } catch (err) {
          console.warn('Immediate auto-save on back error:', err);
        }
      }
    }
    onBack();
  };

  return (
    <div className="package-form-page">
      {/* Top Header */}
      <div className="package-form-header">
        <button type="button" className="btn-form-back" onClick={handleBack}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <h1 className="form-title">{mode === 'create' ? 'Create package' : 'Edit package'}</h1>
        </button>
        <p className="form-subtitle">{'Define the coaching and support your clients receive'}</p>
      </div>

      {/* Main Two-Column Layout */}
      <div className="package-form-layout">
        {/* Left Column: Form Cards */}
        <div className="form-cards-column">
          {/* CARD 1: PACKAGE DETAILS */}
          <div className="form-card">
            <div className="form-card-header">
              <div className="card-heading-left">
                <div className="card-icon-pill">
                  <PackageDetailsIcon className="card-header-icon" />
                </div>
                <h3 className="card-title">{'Package details'}</h3>
              </div>

              {/* Preset Selector */}
              <div className="preset-selector-wrapper">
                <CustomSelect
                  variant="pill"
                  value={presetTier}
                  options={PRESET_OPTIONS}
                  onChange={handlePresetChange}
                  ariaLabel="Select package preset tier"
                />
              </div>
            </div>

            <p className="card-instruction">{'Make your offer easy to understand'}</p>

            <div className="form-fields-stack">
              {/* Package Name */}
              <div className="form-field-group">
                <label htmlFor="package-name-input">{'Package name*'}</label>
                <input
                  id="package-name-input"
                  type="text"
                  className={`text-input ${errors.name ? 'has-error' : ''}`}
                  value={name}
                  maxLength={50}
                  placeholder="e.g. 1-on-1 Coaching"
                  onChange={(e) => {
                    markDirty();
                    setName(e.target.value);
                    if (errors.name) {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.name;
                        return next;
                      });
                    }
                  }}
                  onBlur={(e) => {
                    const trimmed = e.target.value.trim();
                    setName(trimmed);
                    validateField('name', trimmed);
                  }}
                  aria-describedby={errors.name ? 'package-name-error' : undefined}
                />
                {errors.name && (
                  <span id="package-name-error" className="field-error-msg" role="alert">
                    {errors.name}
                  </span>
                )}
              </div>

              {/* Short Description */}
              <div className="form-field-group">
                <label htmlFor="package-desc-input">{'Short description'}</label>
                <input
                  id="package-desc-input"
                  type="text"
                  className={`text-input ${errors.shortDescription ? 'has-error' : ''}`}
                  value={shortDescription}
                  maxLength={200}
                  placeholder="Write a short description"
                  onChange={(e) => {
                    markDirty();
                    setShortDescription(e.target.value);
                    if (errors.shortDescription) {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.shortDescription;
                        return next;
                      });
                    }
                  }}
                  onBlur={(e) => {
                    const trimmed = e.target.value.trim();
                    setShortDescription(trimmed);
                    validateField('shortDescription', trimmed);
                  }}
                  aria-describedby={errors.shortDescription ? 'package-desc-error' : undefined}
                />
                {errors.shortDescription && (
                  <span id="package-desc-error" className="field-error-msg" role="alert">
                    {errors.shortDescription}
                  </span>
                )}
              </div>

              {/* Who is this for? */}
              <div className="form-field-group">
                <label htmlFor="package-who-input">{'Who is this for?'}</label>
                <input
                  id="package-who-input"
                  type="text"
                  className={`text-input ${errors.whoIsThisFor ? 'has-error' : ''}`}
                  value={whoIsThisFor}
                  maxLength={200}
                  placeholder="e.g. Beginners building a consistent routine"
                  onChange={(e) => {
                    markDirty();
                    setWhoIsThisFor(e.target.value);
                    if (errors.whoIsThisFor) {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.whoIsThisFor;
                        return next;
                      });
                    }
                  }}
                  onBlur={(e) => {
                    const trimmed = e.target.value.trim();
                    setWhoIsThisFor(trimmed);
                    validateField('whoIsThisFor', trimmed);
                  }}
                  aria-describedby={errors.whoIsThisFor ? 'package-who-error' : undefined}
                />
                {errors.whoIsThisFor && (
                  <span id="package-who-error" className="field-error-msg" role="alert">
                    {errors.whoIsThisFor}
                  </span>
                )}
              </div>

              {/* Price, Currency, Duration Row */}
              <div className="form-row-3col">
                <div className="form-field-group price-field">
                  <label htmlFor="package-price-input">{'Price*'}</label>
                  <div className={`price-input-wrapper ${errors.price ? 'has-error' : ''}`}>
                    <span className="currency-prefix">{CURRENCY_SYMBOLS[currency] || '£'}</span>
                    <input
                      id="package-price-input"
                      type="text"
                      inputMode="decimal"
                      className="price-input"
                      value={price === '' ? '' : price}
                      placeholder="0"
                      maxLength={8}
                      onChange={(e) => {
                        markDirty();
                        const inputVal = e.target.value.replace(/[^0-9.]/g, '');
                        // Disallow multiple dots
                        const parts = inputVal.split('.');
                        if (parts.length > 2) return;

                        const intPart = parts[0];
                        const decPart = parts[1];

                        // Limit integer part to 5 digits (up to 99999)
                        if (intPart.length > 5) return;

                        // Limit decimal part to 2 digits
                        if (decPart !== undefined && decPart.length > 2) return;

                        // Prevent numeric values exceeding 99999
                        if (inputVal !== '' && inputVal !== '.' && Number(inputVal) > 99999) return;

                        setPrice(inputVal);
                        if (errors.price) {
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next.price;
                            return next;
                          });
                        }
                      }}
                      onBlur={() => {
                        const str = String(price).trim();
                        if (!str) {
                          setPrice('');
                          validateField('price', undefined);
                          return;
                        }
                        let cleaned = str;
                        if (cleaned.endsWith('.')) {
                          cleaned = cleaned.slice(0, -1);
                        }
                        if (cleaned === '' || isNaN(Number(cleaned))) {
                          setPrice('');
                          validateField('price', undefined);
                          return;
                        }
                        setPrice(cleaned);
                        validateField('price', Number(cleaned));
                      }}
                      aria-describedby={errors.price ? 'package-price-error' : undefined}
                    />
                  </div>
                  {errors.price && (
                    <span id="package-price-error" className="field-error-msg" role="alert">
                      {errors.price}
                    </span>
                  )}
                </div>

                <div className="form-field-group">
                  <label htmlFor="package-currency-select">{'Currency'}</label>
                  <CustomSelect
                    id="package-currency-select"
                    variant="input"
                    fullWidth
                    value={currency}
                    options={VALID_CURRENCIES as unknown as string[]}
                    onChange={(val) => {
                      markDirty();
                      setCurrency(val);
                      validateField('currency', val);
                    }}
                    ariaLabel="Select currency"
                  />
                </div>

                <div className="form-field-group">
                  <label htmlFor="package-duration-select">{'Duration'}</label>
                  <CustomSelect
                    id="package-duration-select"
                    variant="input"
                    fullWidth
                    value={duration}
                    options={VALID_DURATIONS}
                    onChange={(val) => {
                      markDirty();
                      setDuration(val);
                      validateField('duration', val);
                    }}
                    ariaLabel="Select duration"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* CARD 2: COACHING & COMMUNICATION */}
          <div className="form-card">
            <div className="form-card-header">
              <div className="card-heading-left">
                <div className="card-icon-pill">
                  <InquiryOrangeIcon className="card-header-icon" />
                </div>
                <h3 className="card-title">{'Coaching & communication'}</h3>
              </div>
            </div>

            <div className="stepper-rows-stack">
              {/* Inquiries / week */}
              <div className="stepper-control-row">
                <div className="stepper-label-col">
                  <span className="stepper-title">{'Inquiries / week*'}</span>
                  <p className="stepper-desc">
                    {'A written summary of recent workouts and next steps.'}
                  </p>
                </div>

                <div className="stepper-widget">
                  <button
                    type="button"
                    className="btn-stepper-sub"
                    onClick={() => handleInquiriesChange(-1)}
                    disabled={inquiriesPerWeek <= 0}
                    aria-label="Decrease inquiries per week"
                  >
                    &minus;
                  </button>
                  <span className="stepper-value-display">{inquiriesPerWeek}</span>
                  <button
                    type="button"
                    className="btn-stepper-add"
                    onClick={() => handleInquiriesChange(1)}
                    disabled={inquiriesPerWeek >= 10}
                    aria-label="Increase inquiries per week"
                  >
                    &#43;
                  </button>
                </div>
              </div>

              {/* Check-ins / week */}
              <div className="stepper-control-row">
                <div className="stepper-label-col">
                  <span className="stepper-title">{'Check-ins / week*'}</span>
                  <p className="stepper-desc">{'Each check-in can take up to 15 min.'}</p>
                </div>

                <div className="stepper-widget">
                  <button
                    type="button"
                    className="btn-stepper-sub"
                    onClick={() => handleCheckInsChange(-1)}
                    disabled={checkInsPerWeek <= 0}
                    aria-label="Decrease check-ins per week"
                  >
                    &minus;
                  </button>
                  <span className="stepper-value-display">{checkInsPerWeek}</span>
                  <button
                    type="button"
                    className="btn-stepper-add"
                    onClick={() => handleCheckInsChange(1)}
                    disabled={checkInsPerWeek >= 10}
                    aria-label="Increase check-ins per week"
                  >
                    &#43;
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* CARD 3: OPTIONAL EXTRAS */}
          <div className="form-card">
            <div className="form-card-header">
              <div className="card-heading-left">
                <div className="card-icon-pill">
                  <OptionalExtrasIcon className="card-header-icon" />
                </div>
                <h3 className="card-title">{'Optional extras'}</h3>
              </div>
            </div>

            <div className="checkboxes-stack">
              {/* Nutritional plan */}
              <label className="checkbox-item-row" htmlFor="nutritional-plan-check">
                <input
                  id="nutritional-plan-check"
                  type="checkbox"
                  className="hidden-checkbox"
                  checked={nutritionalPlan}
                  onChange={(e) => {
                    markDirty();
                    setNutritionalPlan(e.target.checked);
                  }}
                />
                <div className={`custom-checkbox-box ${nutritionalPlan ? 'checked' : ''}`}>
                  {nutritionalPlan && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#04051B"
                      strokeWidth="3"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                <div className="checkbox-label-text">
                  <span className="checkbox-title">{'Nutritional plan'}</span>
                  <p className="checkbox-desc">
                    {'A trainer-prepared plan shared with your client.'}
                  </p>
                </div>
              </label>

              {/* Nutritional guidance */}
              <label className="checkbox-item-row" htmlFor="nutritional-guidance-check">
                <input
                  id="nutritional-guidance-check"
                  type="checkbox"
                  className="hidden-checkbox"
                  checked={nutritionalGuidance}
                  onChange={(e) => {
                    markDirty();
                    setNutritionalGuidance(e.target.checked);
                  }}
                />
                <div className={`custom-checkbox-box ${nutritionalGuidance ? 'checked' : ''}`}>
                  {nutritionalGuidance && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#04051B"
                      strokeWidth="3"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                <div className="checkbox-label-text">
                  <span className="checkbox-title">{'Nutritional guidance'}</span>
                  <p className="checkbox-desc">{'General food-habit guidance during reviews.'}</p>
                </div>
              </label>
            </div>
          </div>

          {/* CARD 4: DELIVERY & EXPECTATIONS */}
          <div className="form-card">
            <div className="form-card-header">
              <div className="card-heading-left">
                <div className="card-icon-pill">
                  <DeliveryExpectationsIcon className="card-header-icon" />
                </div>
                <h3 className="card-title">{'Delivery & expectations'}</h3>
              </div>
            </div>

            <div className="form-fields-stack">
              {/* Initial program ready within */}
              <div className="form-field-group">
                <label htmlFor="initial-ready-within-select">
                  {'Initial program ready within*'}
                </label>
                <div className="delivery-row">
                  <div className="delivery-select-box">
                    <CustomSelect
                      id="initial-ready-within-select"
                      variant="input"
                      isEditable
                      value={initialProgramReadyWithin}
                      options={READY_WITHIN_OPTIONS}
                      placeholder="e.g. Within 3 days"
                      hasError={Boolean(errors.initialProgramReadyWithin)}
                      onChange={(val) => {
                        markDirty();
                        // Strip minus signs and illegal symbols to prevent negative numbers & wrong values
                        const sanitized = val.replace(/[-+!@#$%^&*()_=[\]{};':"\\|,.<>/?`~]/g, '');
                        setInitialProgramReadyWithin(sanitized);
                        if (errors.initialProgramReadyWithin) {
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next.initialProgramReadyWithin;
                            return next;
                          });
                        }
                      }}
                      onBlur={(val) => {
                        const trimmed = val.trim();
                        if (!trimmed) {
                          setInitialProgramReadyWithin('');
                          setErrors((prev) => ({
                            ...prev,
                            initialProgramReadyWithin:
                              'Please select or enter when the initial program will be ready.',
                          }));
                          return;
                        }

                        const validation = validateReadyWithinTimeline(trimmed);
                        if (!validation.isValid && validation.error) {
                          const errorMsg = validation.error;
                          setInitialProgramReadyWithin(trimmed);
                          setErrors((prev) => ({
                            ...prev,
                            initialProgramReadyWithin: errorMsg,
                          }));
                          return;
                        }

                        // Format valid input nicely
                        const match = trimmed.match(/^(?:within\s+)?(\d+)\s*(days?|hours?|d|h)?$/i);
                        if (match) {
                          const count = parseInt(match[1], 10);
                          const unit = match[2]?.toLowerCase();
                          if (
                            unit &&
                            (unit.startsWith('h') || unit === 'hours' || unit === 'hour')
                          ) {
                            setInitialProgramReadyWithin(
                              `Within ${count} ${count === 1 ? 'hour' : 'hours'}`,
                            );
                          } else {
                            setInitialProgramReadyWithin(
                              `Within ${count} ${count === 1 ? 'day' : 'days'}`,
                            );
                          }
                        } else {
                          setInitialProgramReadyWithin(trimmed);
                        }

                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.initialProgramReadyWithin;
                          return next;
                        });
                      }}
                      ariaLabel="Initial program ready within"
                      ariaDescribedBy={
                        errors.initialProgramReadyWithin ? 'initial-ready-error' : undefined
                      }
                    />
                  </div>

                  <span className="delivery-date-note">
                    {getCalculatedStartDate()}
                    {' will be considered the package start date'}
                  </span>
                </div>
                {errors.initialProgramReadyWithin && (
                  <span id="initial-ready-error" className="field-error-msg" role="alert">
                    {errors.initialProgramReadyWithin}
                  </span>
                )}
              </div>

              {/* Nutritional plan delivery (conditional) */}
              {nutritionalPlan && (
                <div className="form-field-group">
                  <label htmlFor="nutritional-delivery-select">{'Nutritional plan delivery'}</label>
                  <div className="nutritional-select-box">
                    <CustomSelect
                      id="nutritional-delivery-select"
                      variant="input"
                      value={nutritionalPlanDelivery}
                      options={NUTRITIONAL_DELIVERY_OPTIONS}
                      placeholder="Set a time"
                      hasError={Boolean(errors.nutritionalPlanDelivery)}
                      onChange={(val) => {
                        markDirty();
                        setNutritionalPlanDelivery(val);
                        if (errors.nutritionalPlanDelivery) {
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next.nutritionalPlanDelivery;
                            return next;
                          });
                        }
                      }}
                      ariaLabel="Nutritional plan delivery"
                      ariaDescribedBy={
                        errors.nutritionalPlanDelivery ? 'nutritional-delivery-error' : undefined
                      }
                    />
                  </div>
                  {errors.nutritionalPlanDelivery && (
                    <span id="nutritional-delivery-error" className="field-error-msg" role="alert">
                      {errors.nutritionalPlanDelivery}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* CARD 5: PACKAGE TERMS */}
          <div className="form-card">
            <div className="form-card-header">
              <div className="card-heading-left">
                <div className="card-icon-pill">
                  <PackageTermsIcon className="card-header-icon" />
                </div>
                <h3 className="card-title">{'Package terms'}</h3>
              </div>
            </div>

            <p className="card-instruction">
              {'These terms will be shown to clients before they accept your package.'}
            </p>

            {/* 2 Summary Tiles */}
            <div className="terms-summary-row">
              <div className="term-info-tile">
                <CalendarIcon className="term-tile-icon" />
                <span className="term-tile-label">
                  {duration.includes('4 weeks')
                    ? '4-week coaching period'
                    : `${duration} coaching period`}
                </span>
              </div>
              <div className="term-info-tile">
                <RefreshIcon className="term-tile-icon" />
                <span className="term-tile-label">{'No automatic renewal'}</span>
              </div>
            </div>

            {/* Document Links */}
            <div className="terms-documents-list">
              <button
                type="button"
                className="doc-link-item"
                onClick={() => onOpenTemplate('checkin_policy', getCurrentDraft())}
              >
                <div className="doc-link-left">
                  <DocIcon className="doc-icon" />
                  <span className="doc-name">{'Check-in policy'}</span>
                </div>
                <span className="doc-arrow">&rsaquo;</span>
              </button>

              <button
                type="button"
                className="doc-link-item"
                onClick={() => onOpenTemplate('contractual_terms', getCurrentDraft())}
              >
                <div className="doc-link-left">
                  <DocIcon className="doc-icon" />
                  <span className="doc-name">{'Contractual terms'}</span>
                </div>
                <span className="doc-arrow">&rsaquo;</span>
              </button>

              <button
                type="button"
                className="doc-link-item"
                onClick={() => onOpenTemplate('cancellation_policy', getCurrentDraft())}
              >
                <div className="doc-link-left">
                  <DocIcon className="doc-icon" />
                  <span className="doc-name">{'Cancellation and unused policy'}</span>
                </div>
                <span className="doc-arrow">&rsaquo;</span>
              </button>
            </div>

            {/* Bottom Disclaimer */}
            <div className="terms-disclaimer-row">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#8e90a6"
                strokeWidth="2"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>{'Client review full terms before accepting their offer.'}</span>
            </div>
          </div>
        </div>

        {/* Right Column: Live Client Preview & Actions */}
        <div className="preview-action-column">
          {/* Client Preview Card */}
          <div className="client-preview-card">
            <span className="preview-column-label">{'CLIENT PREVIEW'}</span>
            <h3 className="preview-pkg-name">{name.trim() || 'Package Name'}</h3>
            <p className="preview-pkg-desc">
              {shortDescription.trim() || 'Package description will appear here'}
            </p>

            <div className="preview-price-block">
              <span className="preview-price-val">
                {CURRENCY_SYMBOLS[currency] || '£'}
                {price !== '' && price !== undefined && !isNaN(Number(price))
                  ? Number(price).toLocaleString('en-GB', {
                      minimumFractionDigits: String(price).includes('.')
                        ? String(price).split('.')[1].length
                        : 0,
                      maximumFractionDigits: 2,
                    })
                  : 0}
              </span>
              <span className="preview-price-period">
                {duration.includes('month') ? ' / month' : ` / ${duration}`}
              </span>
              <span className="preview-renewal-badge">
                {'One-time payment • No automatic renewal'}
              </span>
            </div>

            {/* Preview Feature Items */}
            <div className="preview-features-stack">
              <div className="preview-feature-item">
                <div className="feature-icon-box">
                  <ProgramIcon className="feature-svg" />
                </div>
                <div className="feature-texts">
                  <span className="feature-title">{'Personalized training program'}</span>
                  <p className="feature-subtitle">{'A structured plan tailored to your goals.'}</p>
                </div>
              </div>

              <div className="preview-feature-item">
                <div className="feature-icon-box">
                  <ChatIcon className="feature-svg" />
                </div>
                <div className="feature-texts">
                  <span className="feature-title">
                    {inquiriesPerWeek === 1
                      ? '1 inquirie / week'
                      : `${inquiriesPerWeek} inquiries / week`}
                  </span>
                  <p className="feature-subtitle">{'Detailed feedback on your progress'}</p>
                </div>
              </div>

              <div className="preview-feature-item">
                <div className="feature-icon-box">
                  <VideoIcon className="feature-svg" />
                </div>
                <div className="feature-texts">
                  <span className="feature-title">
                    {`${checkInsPerWeek} x 15-min video check-ins`}
                  </span>
                  <p className="feature-subtitle">{'Stay on track with regular support.'}</p>
                </div>
              </div>

              {nutritionalPlan && (
                <div className="preview-feature-item">
                  <div className="feature-icon-box">
                    <AppleIcon className="feature-svg" />
                  </div>
                  <div className="feature-texts">
                    <span className="feature-title">{'Nutritional plan'}</span>
                    <p className="feature-subtitle">{nutritionalPlanDelivery}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Request this package disabled button */}
            <button type="button" className="btn-request-package-disabled" disabled>
              {'Request this package'}
            </button>

            {/* Security note */}
            <div className="preview-security-note">
              <LockIcon className="lock-icon" />
              <span>{'No payment until your trainer confirms,'}</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="form-action-buttons-wrap">
            <button
              type="button"
              className="btn-publish-orange"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : mode === 'create' ? 'Publish Package' : 'Publish Changes'}
            </button>

            {mode === 'edit' &&
              onUnpublishClick &&
              (initialData?.status ?? 'published') === 'published' && (
                <button
                  type="button"
                  className="btn-unpublish-dark"
                  onClick={onUnpublishClick}
                  disabled={isSaving}
                >
                  {'Unpublish'}
                </button>
              )}

            {/* Auto-save Indicator */}
            <div className="autosave-indicator">
              <CheckmarkGreenIcon className="autosave-check-icon" />
              <span>
                {isAutoSaving ? 'Saving changes...' : 'All changes are saved automatically'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
